<script setup lang="ts">
const homeStore = useHomeStore();
const app = useAppStore();
const betaStore = useBetaStore();
const profile = useProfileStore();
const route = useRoute();
const audioPlayer = useAltAudioPlayback();
const lyricVideoMVP = useLyricVideoMVPStore();

const Error = defineAsyncComponent(() => import("@/components/Error.vue"));

const RecentlyViewed = defineAsyncComponent(
  () => import("@/components/Home/RecentlyViewed.vue"),
);

const PopularSongs = defineAsyncComponent(
  () => import("@/components/Home/PopularSongs.vue"),
);

const ChristmasLinks = defineAsyncComponent(
  () => import("@/components/Home/ChristmasLinks.vue"),
);
const EasterLinks = defineAsyncComponent(
  () => import("@/components/Home/EasterLinks.vue"),
);
const TopSongs = defineAsyncComponent(
  () => import("@/components/Home/TopSongs.vue"),
);

let ChartSongs: any;
if (betaStore.features.ccliTop100New) {
  ChartSongs = defineAsyncComponent(
    () => import("@/components/Home/ChartSongs.vue"),
  );
}

const HomeThemes = defineAsyncComponent(
  () => import("@/components/Home/HomeThemes.vue"),
);
const LiturgyHome = defineAsyncComponent(
  () => import("@/components/Home/LiturgyHome.vue"),
);
const LyricVideosHome = defineAsyncComponent(
  () => import("@/components/Home/LyricVideosHome.vue"),
);
const SongsByDecadeHome = defineAsyncComponent(
  () => import("@/components/Home/SongsByDecadeHome.vue"),
);
const CompanyLinks = defineAsyncComponent(
  () => import("@/components/Home/CompanyLinks.vue"),
);
const SongsByLanguageHome = defineAsyncComponent(
  () => import("@/components/Home/SongsByLanguageHome.vue"),
);
const CCLIOnYoutube = defineAsyncComponent(
  () => import("@/components/Home/CCLIOnYoutube.vue"),
);
const ExclusiveBenefits = defineAsyncComponent(
  () => import("@/components/Home/ExclusiveBenefits.vue"),
);

const status = computed(() => homeStore.status);
const { t } = useI18n();

emitter.on("languageUpdated", init);
onMounted(init);
function init() {
  homeStore.getHomeData();
  updateTitleAndDescription();
}

onActivated(updateTitleAndDescription);

function updateTitleAndDescription() {
  app.updateTitleAndDescription({
    title: t("S.HomePageTabTitle"),
    description: t("S.MetaTagDescription"),
  });
}

const beta = useBetaStore();
const enableRecentlyViewed = computed(() => beta.features.recentlyViewed);

const enableEaster = import.meta.env.VITE_ENABLE_HOME_TOP_EASTER === "1";
const enableChristmas = import.meta.env.VITE_ENABLE_HOME_CHRISTMAS === "1";
const enableSpringHarvestBanner =
  import.meta.env.VITE_ENABLE_SPRING_HARVEST_BANNER === "1";
onMounted(() => detectScrollbarWidth());

watch(route, () => {
  if (route.path !== "/") {
    if (audioPlayer.status) {
      audioPlayer.pause();
    }
  }
});

const enableLyricVideoMVP = computed(() => lyricVideoMVP.enable);
</script>

<template content>
  <Error v-if="status === LoadStatus.Error" />
  <div
    v-else
    class="home-page"
  >
    <Container id="PageHome">
      <h1 class="hidden">{{ $t("S.HomePageTabTitle") }}</h1>

      <AboveBannerMessages />
      <BigHeroBanner />

      <div
        v-if="profile.profile && profile.features"
        class="below-banner"
        :class="{ 'placeholder-glow': homeStore.status === LoadStatus.Loading }"
      >
        <RecentlyViewed
          v-if="enableRecentlyViewed && profile.profile.signedIn"
        />
        <PopularSongs v-if="!betaStore.features.ccliTop100New" />
        <ChartSongs v-if="betaStore.features.ccliTop100New" />
        <SpringHarvestBanner
          v-if="
            enableSpringHarvestBanner &&
            profile.profile.countryCode?.toUpperCase() === 'GB'
          "
        />
        <EasterLinks v-else-if="enableEaster" />

        <LoadWhenVisible>
          <ChristmasLinks v-if="enableChristmas" />
          <CCLIOnYoutube
            v-if="betaStore.features.ccliTop100New && !enableLyricVideoMVP"
          />
          <LyricVideosHome
            v-if="enableLyricVideoMVP"
            see-all-url="/lyric-videos"
            title="Lyric Videos"
            :items="lyricVideoMVP.latestVideos"
          />

          <HomeThemes />
          <TopSongs
            v-if="
              profile.features.enableCCLITopSongs &&
              !betaStore.features.ccliTop100New
            "
          />

          <LiturgyHome />
          <CompanyLinks />
          <CCLIOnYoutube
            v-if="betaStore.features.ccliTop100New && enableLyricVideoMVP"
          />

          <SongsByLanguageHome />
          <SongsByDecadeHome v-if="betaStore.features.ccliTop100New" />

          <CCLIOnYoutube v-if="!betaStore.features.ccliTop100New" />
          <ExclusiveBenefits />
        </LoadWhenVisible>
      </div>
    </Container>
  </div>
</template>

<style scoped lang="postcss">
:deep() {
  section {
    @apply mb-10;
  }
}
.home-page {
  @apply bg-base-100;
}
</style>
